/* eslint-disable lfds/no-raw-token-strings -- --lfds-skeleton-width is an internal custom property from #utils/skeleton.css, not a design token */
import { ifDefined } from 'lit/directives/if-defined.js'
import { classMap } from 'lit/directives/class-map.js'
import { styleMap } from 'lit/directives/style-map.js'
import { live } from 'lit/directives/live.js'
import { property, state } from 'lit/decorators.js'
import { ref } from 'lit/directives/ref.js'
import { html, nothing } from 'lit'
import type { PropertyValues } from 'lit'

import { ariaElements, delegateEvents, refCollection } from '#utils/directives'
import LfdsIcon from '#components/icon/icon/lfds-icon.ts'
import { LfuiElement } from '#utils/element'
import {
	WithInternals,
	WithFocus,
	internals,
	compose,
	anchor
} from '#utils/mixins'
import skeletonStyles from '#utils/skeleton.css'
import { randomSkeletonPercentWidth, skeletonSrLabel } from '#utils/skeleton'

import styles from './lfds-input.css.ts'

const DELEGATED_EVENTS = [
	'input',
	'change',
	'focus',
	'blur',
	'focusin',
	'focusout',
	'keydown',
	'keyup',
	'keypress',
	'paste',
	'cut',
	'copy',
	'select',
	'reset'
]

const AUTOFILL_START_ANIMATION = 'lfds-input-autofill-start'
const AUTOFILL_CANCEL_ANIMATION = 'lfds-input-autofill-cancel'

/**
 * @name LfdsInput
 *
 * @tag lfds-input
 * @status ready
 * @slot label - Input label
 * @slot start - Before the input. Useful for icons and buttons.
 * @slot end - After the input. Useful for icons and buttons.
 */
export default class LfdsInput extends compose(
	LfuiElement,
	WithInternals<HTMLInputElement>,
	WithFocus
) {
	static styles = [LfuiElement.styles, skeletonStyles, styles]

	// React's synthetic `onChange` maps to the DOM `input` event for built-in
	// inputs. By declaring `change` here, @lit/react attaches a native listener,
	// so consumers using <LfdsInput onChange={…}> get the real DOM change event.
	// React 19 improved custom-element handling, but @lit/react remains the
	// recommended path for consistent behavior across React versions.
	static events = ['change']

	@property() value: string | number | File = ''
	@property() type:
		| 'color'
		| 'date'
		| 'datetime-local'
		| 'email'
		| 'file'
		| 'month'
		| 'number'
		| 'password'
		| 'search'
		| 'tel'
		| 'text'
		| 'time'
		| 'url'
		| 'week'
	@property() inputmode:
		'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search'
	@property({ type: Number }) maxlength: number
	@property({ type: Number }) minlength: number
	@property() autocomplete: 'on' | 'off'
	@property() dirname: string
	@property() label!: string
	@property() min: string
	@property() max: string
	@property() name: string
	@property() list: string
	@property({ type: Number }) step: number
	@property({ attribute: 'help-text' }) helpText: string
	@property({ attribute: 'validation-message' }) validationMessage: string
	@property({ attribute: 'start-text' }) startText: string
	@property({ attribute: 'end-text' }) endText: string
	@property({ type: Boolean }) required: boolean
	@property({ type: Boolean }) readonly: boolean = false
	@property({ type: Boolean }) autofocus: boolean = false

	/**
	 * Shows a shimmering placeholder over the input's value text instead of
	 * the real control, for content whose value isn't available yet. The
	 * label and field surface stay visible so the accessible name doesn't
	 * flicker.
	 */
	@property({ type: Boolean, reflect: true }) loading: boolean

	/**
	 * Overrides the generic loading screen-reader description. Pass a
	 * contextual string when this field's content is known (e.g. "Laddar
	 * försäkringsval"), or `''` to suppress the description entirely when a
	 * wrapping region already announces loading for several grouped fields.
	 * Unset keeps the shared generic wording.
	 */
	@property({ attribute: 'sr-loading-label' }) srLoadingLabel?: string

	/**
	 * Remember to set `validation-message` if the input is invalid,
	 * as it is both conveyed to the user and used for the input's setCustomValidity method.
	 */
	@property({ type: Boolean }) invalid: boolean

	@property({ attribute: 'sr-label' }) srLabel: string
	@property({ attribute: 'sr-labelledby' }) srLabelledby: string
	@property({ attribute: 'sr-describedby' }) srDescribedby: string
	@property({ attribute: 'sr-controls' }) srControls: string
	@state() private autofilled = false

	#labels = refCollection()
	#descriptions = refCollection()
	#skeletonWidth = randomSkeletonPercentWidth([45, 60, 70, 80])
	#autofillSyncTimeouts: number[] = []

	constructor() {
		super()
		LfdsIcon.preload('alert', 24)
	}

	connectedCallback() {
		super.connectedCallback()

		// Prevent the host (custom element) from being exposed as a separate node
		// in the accessibility tree. The inner native <input> carries the name,
		// role and value.
		if (!this.hasAttribute('role')) this.setAttribute('role', 'none')

		this[internals].form?.addEventListener('reset', () => {
			this.invalid = false
			this.value = ''
		})

		// Handle calling reportValidity on parent form
		this.addEventListener('invalid', () => {
			this.invalid = true
		})

		// Allow implicit submit when Enter is pressed inside the input.
		// Note: delegateEvents stops native keydown from bubbling out of the
		// shadow root and re-dispatches a CustomEvent from the host instead.
		// Listening on the host lets us catch that delegated event.
		this.addEventListener('keydown', this.#onHostKeydown)
		this.addEventListener('focusin', this.#syncAutofillState)
		this.addEventListener('change', this.#syncAutofillState)

		requestAnimationFrame(() => this.#syncAutofillState())
		this.#autofillSyncTimeouts.push(
			window.setTimeout(() => this.#syncAutofillState(), 250),
			window.setTimeout(() => this.#syncAutofillState(), 1200)
		)
	}

	disconnectedCallback() {
		this.removeEventListener('keydown', this.#onHostKeydown)
		this.removeEventListener('focusin', this.#syncAutofillState)
		this.removeEventListener('change', this.#syncAutofillState)
		for (const timeoutId of this.#autofillSyncTimeouts) {
			window.clearTimeout(timeoutId)
		}
		this.#autofillSyncTimeouts = []
		super.disconnectedCallback()
	}

	focus() {
		this[anchor].value?.focus()
	}

	select() {
		this[anchor].value?.select()
	}

	setSelectionRange(start: number, end: number) {
		this[anchor].value?.setSelectionRange(start, end)
	}

	stepDown() {
		this[anchor].value?.stepDown()
	}

	stepUp() {
		this[anchor].value?.stepUp()
	}

	updated(changed: PropertyValues<this>) {
		if (changed.has('loading')) {
			this.setAttribute('aria-busy', String(!!this.loading))
		}

		if (changed.has('value')) {
			const data = new FormData()
			const value =
				typeof this.value === 'number' ? String(this.value) : this.value
			data.set(this.name, value)
			this[internals].setFormValue(data)
		}

		const input = this[anchor].value
		if (!input) return

		const { minlength } = this
		if (this.validationMessage) {
			this[internals].setValidity(input.validity, this.validationMessage, input)
		} else if (changed.has('value') || minlength) {
			// Native minlength only blocks form submission, not typing, so we still
			// handle it manually to surface a custom message while the user types.
			const value = String(this.value || '')
			let validity = input.validity
			let message = this.validationMessage || input.validationMessage
			if (value.length < minlength) {
				validity = { tooShort: true } as ValidityState
				message = `För få tecken: ${minlength - value.length}`
			}
			this[internals].setValidity(validity, message, input)
		}
	}

	#oninput = (event: Event) => {
		this.value = (event.target as HTMLInputElement).value
		this.invalid = false
		this.#syncAutofillState()
	}

	#onAnimationStart = (event: AnimationEvent) => {
		const input = this[anchor].value
		if (!input || event.target !== input) return

		if (event.animationName === AUTOFILL_START_ANIMATION) {
			this.autofilled = true
			return
		}

		if (event.animationName === AUTOFILL_CANCEL_ANIMATION) {
			this.autofilled = false
		}
	}

	#syncAutofillState = () => {
		const input = this[anchor].value
		if (!input) return

		let next = false
		try {
			next =
				input.matches(':-webkit-autofill') ||
				input.matches(':autofill') ||
				this.#hasNonStandardFilledMarker(input)
		} catch {
			next = this.#hasNonStandardFilledMarker(input)
		}

		if (this.autofilled !== next) {
			this.autofilled = next
		}
	}

	#hasNonStandardFilledMarker(input: HTMLInputElement) {
		// Some browser/extension fills do not expose :autofill but add marker attributes.
		// Keep this generic so we don't couple component styles to any single vendor.
		return Array.from(input.attributes).some(
			({ name }) => name.includes('autofill') || name.endsWith('-filled')
		)
	}

	#onkeydown = (event: KeyboardEvent) => {
		if (event.key === 'Enter' && !event.defaultPrevented) {
			event.preventDefault()
			this[internals].form?.requestSubmit()
		}
	}

	#onHostKeydown = (event: KeyboardEvent | CustomEvent<KeyboardEvent>) => {
		// delegateEvents dispatches a CustomEvent from the host where .detail holds
		// the original KeyboardEvent. Fall back to the event itself if present.
		const originalEvent =
			(event as CustomEvent<KeyboardEvent>).detail || (event as KeyboardEvent)
		this.#onkeydown(originalEvent)
	}

	#onclick() {
		this[anchor].value?.select()
	}

	render() {
		const value = String(this.value ?? '')
		const charsCount = value.length
		const charsLeft = this.maxlength - charsCount
		const ariaLabel = this.srLabel ?? undefined

		return html`
			<div class="header">
				<div class="header-text">
					<label
						class="label"
						for="input"
						${this.#labels.add()}
						@click=${this.#onclick}
					>
						<slot name="label">${this.label}</slot>
					</label>

					${
						this.helpText
							? html`
									<span class="help description" ${this.#descriptions.add()}>
										${this.helpText}
									</span>
								`
							: nothing
					}
				</div>

				${
					this.maxlength
						? html`
								<span
									class=${classMap({
										help: true,
										counter: true,
										warning: charsCount < this.minlength
									})}
								>
									<span aria-hidden="true">
										${charsCount}/${this.maxlength}
									</span>
									<span class="hidden" ${this.#labels.add()}>
										<span aria-live="polite">
											${
												charsLeft < 6 && charsLeft > 0
													? `Antal tecken kvar: ${charsLeft}`
													: nothing
											}
										</span>
										<span aria-live="assertive">
											${charsLeft === 0 ? 'Max antal tecken' : nothing}
										</span>
									</span>
								</span>
							`
						: nothing
				}
			</div>
			<div
				class=${classMap({
					box: true,
					invalid: this.invalid,
					loading: this.loading,
					autofilled: this.autofilled,
					['focus-visible']: this.focusVisible
				})}
			>
				<slot name="start">
					${
						this.startText
							? html`
									<span
										class="affix prefix"
										aria-hidden="true"
										@click=${this.#onclick}
									>
										${this.startText}
									</span>
								`
							: nothing
					}
				</slot>
				<input
					id="input"
					class="input"
					placeholder=" "
					style=${styleMap({
						'--lfds-skeleton-width': `${this.#skeletonWidth}%`
					})}
					aria-invalid=${ifDefined(this.invalid ? 'true' : undefined)}
					aria-label=${ifDefined(ariaLabel)}
					autocomplete=${ifDefined(this.autocomplete)}
					dirname=${ifDefined(this.dirname)}
					inputmode=${ifDefined(this.inputmode)}
					max=${ifDefined(this.max)}
					maxlength=${ifDefined(this.maxlength)}
					min=${ifDefined(this.min)}
					step=${ifDefined(this.step)}
					list=${ifDefined(this.list)}
					name=${ifDefined(this.name)}
					type=${ifDefined(this.type)}
					?autofocus=${this.autofocus}
					?required=${this.required}
					?readonly=${this.readonly}
					?disabled=${this.loading}
					@input=${this.#oninput}
					@animationstart=${this.#onAnimationStart}
					.value=${live(value)}
					${ref(this[anchor])}
					${this.#labels.bind('ariaLabelledByElements')}
					${this.#descriptions.bind('ariaDescribedByElements')}
					${delegateEvents(DELEGATED_EVENTS, {
						before: { input: this.#oninput }
					})}
					${ariaElements({
						describedBy: this.srDescribedby,
						labelledBy: this.srLabelledby,
						controls: this.srControls
					})}
				/>
				<slot name="end">
					${
						this.endText
							? html`
									<span
										class="affix suffix"
										aria-hidden="true"
										@click=${this.#onclick}
									>
										${this.endText}
									</span>
								`
							: nothing
					}
				</slot>
			</div>
			${
				this.validationMessage
					? html`
							<div aria-atomic="true" role="alert" class="validation">
								<span class="icon">
									<lfds-icon
										icon-id="alert"
										size="24"
										color="currentcolor"
									></lfds-icon>
								</span>
								<span ${this.#labels.add()}>${this.validationMessage}</span>
							</div>
						`
					: nothing
			}
			${
				this.loading && skeletonSrLabel(this, this.srLoadingLabel)
					? html`<span class="hidden" role="status" ${this.#descriptions.add()}>
							${skeletonSrLabel(this, this.srLoadingLabel)}
						</span>`
					: nothing
			}
		`
	}
}
